import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import styles from './ErrorBoundary.module.scss'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Top-level error boundary. Catches render errors anywhere in the app
 * tree and shows a friendly Italian fallback rather than blanking the
 * page. The original error is exposed in a collapsed <details> so
 * support can read it from the user's screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children

    return (
      <main className={styles.page} data-app="progetto_arca">
        <section className={styles.card} aria-labelledby="error-title">
          <div className={styles.header}>
            <span className={styles.iconWrap} aria-hidden>
              <AlertTriangle size={20} />
            </span>
            <h1 id="error-title" className={styles.title}>
              Si è verificato un errore
            </h1>
          </div>
          <p className={styles.body}>
            L'applicazione ha incontrato un problema imprevisto. Prova a ricaricare la pagina;
            se il problema persiste, contatta il supporto.
          </p>
          <div className={styles.actions}>
            <Button type="button" onClick={() => window.location.reload()}>
              Ricarica
            </Button>
            <Button type="button" variant="outline" onClick={this.reset}>
              Riprova
            </Button>
          </div>
          <details className={styles.details}>
            <summary className={styles.summary}>Dettagli tecnici</summary>
            <pre className={styles.pre}>{this.state.error.stack ?? this.state.error.message}</pre>
          </details>
        </section>
      </main>
    )
  }
}
